import {
    Brand,
    ClientWithBrands,
    Country,
    Direction,
    EMPTY_BRAND,
    EMPTY_CLIENT,
    EMPTY_DIRECTION,
    EMPTY_NETWORK,
    EMPTY_WORK_TYPE,
    Network,
    WorkType
} from './refs/refs'
import { EMPTY_USER, User, UserWithDepartments } from './users'
import { FileData } from './files'
import { Bool, ID, UpdateData } from '../types/UtilityTypes'
import { Query, SuccessResponse } from './response'
import { Overwrite } from 'utility-types'
import { TaskStatus } from '../constants/enums'
import { FullObject, ObjectCloseData, ProjectWorkObject } from './objects'
import { CommentData } from './comments'
import { Colors } from '../styles'
import { enumIs, enumMatchAll } from '../utils/enum-utils'
import type { AbstractQuery } from './tasks'

export enum ProjectStatus {
    ALL = -1,
    NEW = 10,
    IN_WORK = 20,
    TO_CLOSE = 30,
    PAUSED = 35,
    TO_ARCHIVE = 37,
    CLOSED = 40,
    CANCELED = 50,
    ARCHIVED = 70,
    RESERVED = 80,
}

export enum ProjectProcessStatus {
    COMPLETE = 0,
    WAITING = 10,
    IN_PROGRESS = 20,
    ERROR = 30
}

export const projectStatusToColor = enumMatchAll<ProjectStatus>()({
    [ProjectStatus.ALL]: 'rgb(0,0,0)',
    [ProjectStatus.NEW]: Colors.orange,
    [ProjectStatus.IN_WORK]: Colors.blue,
    [ProjectStatus.TO_CLOSE]: Colors.darkBlue,
    [ProjectStatus.PAUSED]: Colors.grey,
    [ProjectStatus.CLOSED]: Colors.green,
    [ProjectStatus.CANCELED]: Colors.olive,
    [ProjectStatus.TO_ARCHIVE]: Colors.purpleLight,
    [ProjectStatus.ARCHIVED]: Colors.purpleMedium,
    [ProjectStatus.RESERVED]: Colors.purpleDark,
})

export const inArchiveStage = enumIs<ProjectStatus>()([ProjectStatus.TO_ARCHIVE, ProjectStatus.ARCHIVED, ProjectStatus.RESERVED])

export const projectStatusToName = enumMatchAll<ProjectStatus>()({
    [ProjectStatus.ALL]: 'все',
    [ProjectStatus.NEW]: 'Новый',
    [ProjectStatus.IN_WORK]: 'В работе',
    [ProjectStatus.TO_CLOSE]: 'К закрытию',
    [ProjectStatus.PAUSED]: 'На паузе',
    [ProjectStatus.CLOSED]: 'Закрыт',
    [ProjectStatus.CANCELED]: 'Отменён',
    [ProjectStatus.TO_ARCHIVE]: 'К архивации',
    [ProjectStatus.ARCHIVED]: 'Архивный',
    [ProjectStatus.RESERVED]: 'На стримере',
})

export interface Project {
    id: ID,
    code: string,
    name: string,
    status_id: ProjectStatus,
    process_status_id: ProjectProcessStatus,
    process_status_name: string,
    short_name: string,
    worksection?: string,
    description: string | null,
    opened_at: string,
    closed_at?: string,
    deleted_at?: string,
    direction: Direction,
    manager: User,
    client: ClientWithBrands,
    brand: Brand,
    countries: Country[],
    work_type: WorkType | null,
    network: Network | null,
    users: UserWithDepartments[],
    tasks_users?: User[],
    files: FileData[],
    work_objects_count: number,
    tasks_count: number,
    related?: RelatedProject[] | null,
    finplan?: RelatedProject,
    dir: string,
    dir_exists: Bool,
    has_client: Bool,
    folder: string,
    unsubscribed_users: ID[],
    imported: boolean,
    size: number,
    unitized_size: string,
}

export interface ServerProject extends Project {
    project_work_objects: ProjectWorkObject[],
}

export interface FullProject extends Project {
    project_work_objects: FullObject[],
}

export type ProjectUpdateData = Overwrite<UpdateData<ServerProject>, {
    related?: {
        id: ID,
        is_design?: 0 | 1,
    }[]
}>

export interface ProjectCloseData {
    id: ID,
    project_work_objects: ObjectCloseData[],
    comment?: CommentData,
}

export const EMPTY_PROJECT: Project = {
    brand: EMPTY_BRAND,
    client: EMPTY_CLIENT,
    countries: [],
    description: '',
    direction: EMPTY_DIRECTION,
    files: [],
    id: -1,
    code: '',
    manager: EMPTY_USER,
    name: '',
    network: EMPTY_NETWORK,
    opened_at: '',
    short_name: '',
    status_id: ProjectStatus.NEW,
    process_status_id: ProjectProcessStatus.COMPLETE,
    process_status_name: 'Завершен',
    users: [],
    work_type: EMPTY_WORK_TYPE,
    worksection: '',
    work_objects_count: 0,
    tasks_count: 0,
    dir: '',
    dir_exists: 1,
    has_client: 1,
    folder: '',
    unsubscribed_users: [],
    imported: false,
    size: 0,
    unitized_size: '0Kb'
}

export type ProjectSortKeys = 'opened_at' | 'code' | 'short_name' | 'status_id' | 'priority' | 'last_comment'

export type TipIdKeys = 'manager' | 'tasks_target_user' |
    'client' | 'brand' | 'network' | 'country' | 'work_object' | 'tasks_type' | 'work_type' | 'object_curator'

export type TipKey = TipIdKeys | 'opened_at' | 'code' | 'search' | 'status' | 'tasks_status'

export type ProjectListQuery = Query & {
    archive?: Bool,
    opened_at?: string | [string, string],
    code?: string,
    search_full?: string,
    status?: ProjectStatus[],
    tags?: ID[],
    parents?: boolean,
    tasks_status?: TaskStatus,
    object_design_status?: number,
    object_production_status?: number
} & {
    [key in TipIdKeys]?: AbstractQuery
}

export interface RelatedProject {
    id: ID,
    code: string,
    name: string,
    is_design: 0 | 1,
    link: string,
    direction: {
        id: number,
        title: string,
    }
}

export const relatedToString = (project?: RelatedProject, withName?: boolean): string => {
    if (!project?.id) {
        return ''
    }
    return (project.is_design ? '' : '№') + (project.code || '') + '/== ' + (withName ? project.name : project.direction?.title)
}

export type ProjectRecord = Record<ID, Project>

export type ProjectSize = {
    value: number,
    unitized: string,
}

export interface ReservableResponse extends SuccessResponse {
    rows: ServerProject[],
    reserve_disk_number: string,
    max_size: ProjectSize,
    min_size: ProjectSize,
    total: number,
}